/** @jsx jsx */
import { jsx } from "@emotion/react";
import * as Sentry from "@sentry/react";
import { injectIntl, WrappedComponentProps as IntlProps } from "react-intl";
import { ErrorInfo, PureComponent, ReactNode } from "react";
import { BugReport, Home, Refresh } from "@material-ui/icons";
import { RouteComponentProps, withRouter } from "react-router";
import { actionContainerCss, actionCss, OuterContainer } from "./styles";
import { BoundaryErrorData } from "../../../store/bugReport/types";

type Props = {
  children: ReactNode;
  inline?: boolean;
  withYMargin?: boolean;
  noStretch?: boolean;
  pushErrorToBugReportStack: (error: BoundaryErrorData) => void;
  invokeBugReportModal: (error: BoundaryErrorData) => void;
};

type TotalProps = RouteComponentProps & IntlProps & Props;

type State = {
  hasError: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lastError: null | any;
};

class FlowioErrorBoundary extends PureComponent<TotalProps, State> {
  state: State = {
    hasError: false,
    lastError: null,
  };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, lastError: error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.pushErrorToBugReportStack({
      message: error.message,
      name: error.name,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  remountSubtree = () => this.setState({ hasError: false });

  goToHomepage = () => {
    this.props.history.push("/");
    this.setState({ hasError: false });
  };

  reportBug = () => this.props.invokeBugReportModal(this.state.lastError);

  renderOnError = () => {
    const { intl, inline, noStretch, withYMargin } = this.props;

    const fallbackMsg = () => (
      <p>{intl.formatMessage({ id: "error_boundary.something_went_wrong" })}</p>
    );

    return (
      <Sentry.ErrorBoundary showDialog fallback={fallbackMsg()}>
        <OuterContainer
          inline={inline}
          withYMargin={withYMargin}
          noStretch={noStretch}
        >
          {fallbackMsg()}
          <div css={actionContainerCss}>
            <Refresh onClick={this.remountSubtree} css={actionCss} />
            <Home onClick={this.goToHomepage} css={actionCss} />
            <BugReport onClick={this.reportBug} css={actionCss} />
          </div>
        </OuterContainer>
      </Sentry.ErrorBoundary>
    );
  };

  render() {
    if (this.state.hasError) {
      return this.renderOnError();
    }

    return this.props.children;
  }
}

export default withRouter(injectIntl(FlowioErrorBoundary));
export type ErrorBoundaryProps = Props;
